import { useCallback, useEffect, useRef, useState } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { fetchSceneRow } from '@/modules/databaseService/scenes';
import { shareLinkUrl, type ShareLinkDto } from '@/modules/databaseService/scenes/shareLinks';
import { getPublishedDevices } from '@/components/publish-flow/deviceAvailability';
import { getEffectiveBuildDevice } from '@/constants/buildDevices';
import { ApiProblem } from '@/infrastructure/api/v1/problem';
import { onAccountChanges, type AccountChangeEvent } from '@/modules/mcpStream';
import type { SceneService } from '@/modules/databaseService/types';

type ShareLinkReads = Pick<SceneService, 'getShareLinks' | 'mintDraftShareLink'>;

export interface ShareLinkState {
  /** The version's link: its preview link while unpublished (draft or in review), the story's permanent link while live. */
  link: ShareLinkDto | null;
  url: string | null;
  sceneOriginId: string | null;
  status: string | null;
  /** Why there is no link, when there is none and nothing failed. */
  reason: string | null;
  /** True when the story is live without a permanent link, so one can be created on demand. */
  canCreate: boolean;
  /** Whether the author chose Spectacles: a live version's published platforms, else the build target. */
  forSpectacles: boolean;
  error: string | null;
  errorCode: string | null;
}

const EMPTY: ShareLinkState = {
  link: null, url: null, sceneOriginId: null, status: null, reason: null, canCreate: false, forSpectacles: false, error: null, errorCode: null,
};

// Only a live version's platform array is a published choice: the publish flow writes the array
// before validating, so a failed attempt can leave one on a draft that says nothing about its target
function isForSpectacles(payload: Record<string, unknown> | string | null | undefined, status: string): boolean {
  const published = status === 'live' ? getPublishedDevices(payload) : null;
  return published ? published.includes('spectacles') : getEffectiveBuildDevice(payload) === 'spectacles';
}

export const NO_LIVE_LINK_REASON = 'This story has no public link right now. Create one to share it.';

export const TAKEN_DOWN_NOTE = "Lureo has taken this story down, so its links and PIN don't open it.";

/** A version with a preview link: a draft, or a draft submitted for review (the backend keeps it). */
export function isPreviewStatus(status: string | null | undefined): boolean {
  return status === 'draft' || status === 'pending';
}

// A load started before the latest invalidation is dropped, so a slow answer cannot put a dead link back
type Revalidate = 'scene' | 'full';

const results = new Map<string, ShareLinkState>();
const failures = new Map<string, ShareLinkState>();
const generations = new Map<string, number>();
const inflight = new Set<string>();
const stale = new Map<string, Revalidate>();
const autoRetried = new Set<string>();
const subscribers = new Map<string, Set<() => void>>();

const AUTO_RETRY_MS = 3_000;
const AUTO_RETRY_MAX_MS = 30_000;

function notify(id: string) {
  subscribers.get(id)?.forEach((cb) => cb());
}

function bump(id: string) {
  generations.set(id, (generations.get(id) ?? 0) + 1);
  inflight.delete(id);
}

function drop(id: string) {
  bump(id);
  results.delete(id);
  failures.delete(id);
  stale.delete(id);
  notify(id);
}

function markStale(id: string, mode: Revalidate) {
  if (!subscribers.has(id)) {
    drop(id);
    return;
  }
  if (failures.has(id) || !results.has(id)) mode = 'full';
  bump(id);
  failures.delete(id);
  stale.set(id, stale.get(id) === 'full' ? 'full' : mode);
  notify(id);
}

function knownIds(): string[] {
  return [...new Set([...results.keys(), ...failures.keys(), ...inflight, ...subscribers.keys()])];
}

function originOf(id: string): string | null {
  return (results.get(id) ?? failures.get(id))?.sceneOriginId ?? null;
}

/** Drop what is cached for a version, so the next render refetches. Used after a swap, revoke or status change. */
export function clearShareLinkCache(sceneInstanceId: string) {
  drop(sceneInstanceId);
}

// Call after a status change made in this tab: the account stream skips the tab's own writes
export function invalidateShareLinks(storyId?: string | null) {
  for (const id of knownIds()) {
    const origin = originOf(id);
    if (!storyId || !origin || origin === storyId) drop(id);
  }
}

function staleAll(mode: Revalidate) {
  for (const id of knownIds()) markStale(id, mode);
}

function staleStory(storyId: string, mode: Revalidate) {
  for (const id of knownIds()) {
    const origin = originOf(id);
    if (!origin || origin === storyId) markStale(id, mode);
  }
}

// shareLink events name the story the link opens; scene events name the version
function onChanges(events: AccountChangeEvent[]) {
  if (events.length === 0) {
    staleAll('full');
    return;
  }
  for (const event of events) {
    const id = event.entityUniqueId;
    if (event.entity === 'shareLink') {
      if (id) staleStory(id, 'full');
      else staleAll('full');
    } else if (event.entity === 'scene') {
      if (!id) {
        staleAll('scene');
        continue;
      }
      const origin = originOf(id);
      if (origin) staleStory(origin, 'scene');
      else if (knownIds().includes(id)) markStale(id, 'scene');
    }
  }
}

const stopListening = onAccountChanges(['shareLink', 'scene'], onChanges);
import.meta.hot?.dispose(stopListening);

function withLink(state: ShareLinkState, link: ShareLinkDto | null): ShareLinkState {
  return { ...state, link, url: link ? shareLinkUrl(link.slug) : null, reason: null, canCreate: false, error: null, errorCode: null };
}

function retryDelay(err: unknown): number {
  const after = err instanceof ApiProblem ? err.retryAfterSeconds : null;
  return after ? Math.min(after * 1000, AUTO_RETRY_MAX_MS) : AUTO_RETRY_MS;
}

async function readState(id: string, scene: ShareLinkReads, mode: Revalidate | undefined, onBase: (base: ShareLinkState) => void): Promise<ShareLinkState> {
  const { data, error } = await fetchSceneRow(id);
  if (error) throw error;
  const originId = data.scene_origin_id ?? null;
  const status = (data.scene_instance_status ?? '').toLowerCase();
  const forSpectacles = isForSpectacles(data.scene_instance_payload, status);
  const base: ShareLinkState = { ...EMPTY, sceneOriginId: originId, status, forSpectacles };
  onBase(base);

  // A scene change that left the status alone leaves the link alone: link changes arrive as shareLink events
  const previous = results.get(id);
  if (mode === 'scene' && previous && previous.status === status && previous.sceneOriginId === originId) {
    return { ...previous, forSpectacles };
  }

  if (!originId) return { ...base, reason: 'This version has no story record, so it cannot have a link.' };
  if (isPreviewStatus(status)) return withLink(base, await scene.mintDraftShareLink(id));
  if (status === 'live') {
    const { live } = await scene.getShareLinks(originId);
    return live ? withLink(base, live) : { ...base, reason: NO_LIVE_LINK_REASON, canCreate: true };
  }
  return { ...base, reason: 'Only unpublished and live versions have links.' };
}

function load(id: string, scene: ShareLinkReads) {
  const generation = generations.get(id) ?? 0;
  const mode = stale.get(id);
  inflight.add(id);
  let base: ShareLinkState = EMPTY;
  const current = () => (generations.get(id) ?? 0) === generation;

  void readState(id, scene, mode, (b) => { base = b; })
    .then((state) => {
      if (!current()) return;
      results.set(id, state);
      failures.delete(id);
      autoRetried.delete(id);
    })
    .catch((err: unknown) => {
      if (!current()) return;
      const failure: ShareLinkState = {
        ...base,
        error: err instanceof Error ? err.message : String(err),
        errorCode: err instanceof ApiProblem ? err.code : null,
      };
      results.delete(id);
      failures.set(id, failure);
      if (!autoRetried.has(id) && failure.errorCode !== 'taken_down') {
        autoRetried.add(id);
        setTimeout(() => {
          if (failures.get(id) !== failure || !subscribers.has(id)) return;
          failures.delete(id);
          notify(id);
        }, retryDelay(err));
      }
    })
    .finally(() => {
      if (!current()) return;
      inflight.delete(id);
      stale.delete(id);
      notify(id);
    });
}

// A write's answer is the newest truth: it supersedes any read still on its way
function settle(id: string, next: ShareLinkState) {
  bump(id);
  results.set(id, next);
  failures.delete(id);
  stale.delete(id);
  notify(id);
}

/**
 * What a screen's one read says of a live version's link (getStoryWorkspace: the story's versions and, for its owner,
 * the story's active links), kept as this version's link as its own read would find it. A version whose link was read
 * already, or is being read, keeps that.
 */
export function seedLiveShareLink(sceneInstanceId: string, sceneOriginId: string, payload: Record<string, unknown> | string | null | undefined, links: ShareLinkDto[]) {
  const id = sceneInstanceId;
  if (results.has(id) || failures.has(id) || inflight.has(id) || stale.has(id)) return;
  const base: ShareLinkState = { ...EMPTY, sceneOriginId, status: 'live', forSpectacles: isForSpectacles(payload, 'live') };
  const live = links.find(link => link.kind === 'live') ?? null;
  results.set(id, live ? withLink(base, live) : { ...base, reason: NO_LIVE_LINK_REASON, canCreate: true });
  notify(id);
}

function needsLoad(id: string): boolean {
  if (inflight.has(id)) return false;
  return stale.has(id) || (!results.has(id) && !failures.has(id));
}

/**
 * The share link for a version (share links 3.4, 3.5). A module-level cache so
 * every surface showing the same version agrees, and so the editor's Test dialog can gate its
 * content on `ready` instead of flashing a stale or missing link.
 *
 * A draft gets its own preview link the first time it is shared (the mint is idempotent, so opening
 * Test is what mints it), and keeps it while it is in review. A live version shows the story's
 * permanent link, which the backend mints when the version goes live; if the story has none (it was
 * revoked, or that mint failed) the hook says so and `createLive` mints one explicitly rather than
 * silently undoing a revoke. Archived and template versions have no link by design.
 */
export function useShareLink(
  sceneInstanceId: string | null | undefined,
  options: { expectedStatus?: string | null } = {},
): ShareLinkState & {
  ready: boolean;
  linkUnknown: boolean;
  refetch: () => void;
  rotate: () => Promise<ShareLinkDto | null>;
  revoke: () => Promise<void>;
  createLive: () => Promise<ShareLinkDto | null>;
} {
  const services = useServices();
  const [tick, forceUpdate] = useState(0);
  const expectedStatus = options.expectedStatus?.toLowerCase() ?? null;
  const rereadFor = useRef<string | null>(null);

  useEffect(() => {
    if (!sceneInstanceId) return;
    const subSet = subscribers.get(sceneInstanceId) ?? new Set<() => void>();
    const onChange = () => forceUpdate((n) => n + 1);
    subSet.add(onChange);
    subscribers.set(sceneInstanceId, subSet);
    return () => {
      subSet.delete(onChange);
      if (subSet.size > 0) return;
      subscribers.delete(sceneInstanceId);
      failures.delete(sceneInstanceId);
      autoRetried.delete(sceneInstanceId);
    };
  }, [sceneInstanceId]);

  useEffect(() => {
    if (!sceneInstanceId || !subscribers.has(sceneInstanceId)) return;
    const cached = results.get(sceneInstanceId);
    const key = `${sceneInstanceId}|${expectedStatus}`;
    if (expectedStatus && cached && cached.status !== expectedStatus && !inflight.has(sceneInstanceId) && rereadFor.current !== key) {
      rereadFor.current = key;
      markStale(sceneInstanceId, 'full');
      return;
    }
    if (needsLoad(sceneInstanceId)) load(sceneInstanceId, services.scene);
  }, [sceneInstanceId, services.scene, tick, expectedStatus]);

  const id = sceneInstanceId ?? null;
  const result = id ? results.get(id) : undefined;
  const failure = id ? failures.get(id) : undefined;
  const state = result ?? failure ?? EMPTY;
  const ready = !id || Boolean(result || failure);
  const linkUnknown = Boolean(id) && !result;

  const refetch = useCallback(() => {
    if (sceneInstanceId) clearShareLinkCache(sceneInstanceId);
  }, [sceneInstanceId]);

  const rotate = useCallback(async () => {
    if (!sceneInstanceId) return null;
    const current = results.get(sceneInstanceId);
    if (!current?.link) return null;
    const fresh = await services.scene.rotateShareLink(current.link.slug);
    settle(sceneInstanceId, withLink(results.get(sceneInstanceId) ?? current, fresh));
    return fresh;
  }, [sceneInstanceId, services.scene]);

  const revoke = useCallback(async () => {
    if (!sceneInstanceId) return;
    const current = results.get(sceneInstanceId)?.link;
    if (!current) return;
    await services.scene.revokeShareLink(current.slug);
    clearShareLinkCache(sceneInstanceId);
  }, [sceneInstanceId, services.scene]);

  const createLive = useCallback(async () => {
    if (!sceneInstanceId) return null;
    const current = results.get(sceneInstanceId);
    if (!current?.sceneOriginId || current.status !== 'live') return null;
    try {
      const link = await services.scene.mintLiveShareLink(current.sceneOriginId);
      settle(sceneInstanceId, withLink(current, link));
      return link;
    } catch (err) {
      // A takedown revokes the live link, so the story reads as one without a link until the mint says why
      if (err instanceof ApiProblem && err.code === 'taken_down') {
        bump(sceneInstanceId);
        results.delete(sceneInstanceId);
        failures.set(sceneInstanceId, { ...current, canCreate: false, reason: null, error: err.message, errorCode: err.code });
        notify(sceneInstanceId);
      }
      throw err;
    }
  }, [sceneInstanceId, services.scene]);

  return { ...state, ready, linkUnknown, refetch, rotate, revoke, createLive };
}
